/* =============================================================
   cs-otp-login.js — Phone OTP login / signup popup
   Intercepts guest account icon clicks and drives a 3-step flow:
     1. Phone number entry → calls Worker /otp/send
     2. OTP verification  → calls Worker /otp/verify
     3. Profile (name + email) for NEW customers → calls Worker /customer/create
   After auth: fetches CSRF token from /account/login, submits hidden form.
   Worker URL is set via theme setting: window.csOtpWorkerUrl
   ============================================================= */

(function () {
  'use strict';

  /* ── Config injected from Liquid via theme.liquid ── */
  var WORKER_URL = (window.csOtpWorkerUrl || '').replace(/\/$/, '');

  if (!WORKER_URL) {
    // OTP system disabled or worker URL not configured — do nothing
    return;
  }

  /* ── State ── */
  var _phone          = '';
  var _resendTimer    = null;
  var _resendCount    = 0;
  var _currentStep    = 'phone';

  /* ── Element refs ── */
  var overlay         = document.getElementById('cs-otp-overlay');
  if (!overlay) return;  // Not rendered (customer logged in or feature disabled)

  var modal           = overlay.querySelector('.cs-otp-modal');
  var steps           = {};
  overlay.querySelectorAll('.cs-otp-step[data-step]').forEach(function (el) {
    steps[el.getAttribute('data-step')] = el;
  });

  /* ── Open / Close ── */

  function openPopup() {
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    showStep('phone');
    var phoneInput = overlay.querySelector('#cs-otp-phone');
    if (phoneInput) setTimeout(function () { phoneInput.focus(); }, 320);
  }

  window.csOtpOpen = openPopup;

  function closePopup() {
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    resetError('phone');
    resetError('otp');
    resetError('profile');
  }

  /* ── Step navigation ── */

  function showStep(name) {
    _currentStep = name;
    Object.keys(steps).forEach(function (key) {
      var stepEl = steps[key];
      if (key === name) {
        stepEl.hidden = false;
        stepEl.removeAttribute('aria-hidden');
      } else {
        stepEl.hidden = true;
        stepEl.setAttribute('aria-hidden', 'true');
      }
    });
  }

  /* ── Error display ── */

  function setError(step, msg) {
    var errEl = overlay.querySelector('[data-cs-otp-error="' + step + '"]');
    if (errEl) errEl.textContent = msg || '';
  }

  function resetError(step) { setError(step, ''); }

  /* ── Button loading state ── */

  function setBusy(btn, busy) {
    if (!btn) return;
    if (busy) {
      btn.classList.add('cs-otp-btn--loading');
      btn.disabled = true;
    } else {
      btn.classList.remove('cs-otp-btn--loading');
      btn.disabled = false;
    }
  }

  /* ── Worker API calls ── */

  function apiCall(path, body, callback) {
    fetch(WORKER_URL + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (data) { callback(null, data); })
      .catch(function (err) { callback(err, null); });
  }

  /* ── Step 1: Send OTP ── */

  function handleSendOtp() {
    resetError('phone');
    var phoneInput = overlay.querySelector('#cs-otp-phone');
    var phone      = (phoneInput ? phoneInput.value : '').trim().replace(/\D/g, '');

    if (phone.length !== 10) {
      setError('phone', 'Please enter a valid 10-digit mobile number.');
      return;
    }

    _phone = phone;
    var btn = overlay.querySelector('[data-cs-otp-action="send"]');
    setBusy(btn, true);

    apiCall('/otp/send', { phone: _phone }, function (err, data) {
      setBusy(btn, false);

      if (err || !data || data.error) {
        setError('phone', (data && data.error) || 'Could not send OTP. Please try again.');
        return;
      }

      // Update the "sent to" label
      var sentTo = overlay.querySelector('#cs-otp-sent-to strong');
      if (sentTo) sentTo.textContent = '+91 ' + _phone.replace(/(\d{5})(\d{5})/, '$1 $2');

      showStep('otp');
      startResendTimer();
      clearDigits();

      // Focus first digit
      setTimeout(function () {
        var firstDigit = overlay.querySelector('.cs-otp-digit');
        if (firstDigit) firstDigit.focus();
      }, 100);
    });
  }

  /* ── Step 2: Verify OTP ── */

  function handleVerifyOtp() {
    resetError('otp');
    var otp = getOtpValue();

    if (otp.length !== 6) {
      setError('otp', 'Please enter the complete 6-digit OTP.');
      return;
    }

    var btn = overlay.querySelector('[data-cs-otp-action="verify"]');
    setBusy(btn, true);

    apiCall('/otp/verify', { phone: _phone, otp: otp }, function (err, data) {
      setBusy(btn, false);

      if (err || !data) {
        setError('otp', 'Verification failed. Please try again.');
        return;
      }

      if (data.error) {
        setError('otp', data.error);
        return;
      }

      clearResendTimer();

      if (data.status === 'existing') {
        saveSession(data);
        doLogin(data.email, data.password);
      } else if (data.status === 'new') {
        // New customer — collect profile
        showStep('profile');
        setTimeout(function () {
          var nameInput = overlay.querySelector('#cs-otp-name');
          if (nameInput) nameInput.focus();
        }, 100);
      } else {
        setError('otp', 'Unexpected response from server. Please try again.');
      }
    });
  }

  /* ── Step 3: Create new customer ── */

  function handleCreateCustomer() {
    resetError('profile');
    var nameInput  = overlay.querySelector('#cs-otp-name');
    var emailInput = overlay.querySelector('#cs-otp-email');
    var name       = (nameInput  ? nameInput.value  : '').trim();
    var email      = (emailInput ? emailInput.value : '').trim();

    if (!name) {
      setError('profile', 'Please enter your name.');
      if (nameInput) {
        nameInput.classList.add('cs-otp-field__input--error');
        nameInput.focus();
      }
      return;
    }

    if (email && !isValidEmail(email)) {
      setError('profile', 'Please enter a valid email address.');
      if (emailInput) {
        emailInput.classList.add('cs-otp-field__input--error');
        emailInput.focus();
      }
      return;
    }

    var btn = overlay.querySelector('[data-cs-otp-action="create"]');
    setBusy(btn, true);

    apiCall('/customer/create', { phone: _phone, name: name, email: email || '' }, function (err, data) {
      setBusy(btn, false);

      if (err || !data) {
        setError('profile', 'Account creation failed. Please try again.');
        return;
      }

      if (data.error) {
        setError('profile', data.error);
        return;
      }

      saveSession(data);
      doLogin(data.email, data.password);
    });
  }

  /* ── Resend OTP ── */

  function handleResend() {
    if (_resendCount >= 3) {
      setError('otp', 'Maximum resend attempts reached. Please try a different number.');
      return;
    }
    _resendCount++;
    resetError('otp');
    clearDigits();

    var resendBtn   = overlay.querySelector('.cs-otp-resend__btn');
    var resendTimer = overlay.querySelector('.cs-otp-resend__timer');
    if (resendBtn)   resendBtn.hidden = true;
    if (resendTimer) resendTimer.hidden = false;

    apiCall('/otp/send', { phone: _phone }, function (err, data) {
      if (err || !data || data.error) {
        setError('otp', (data && data.error) || 'Could not resend OTP.');
        return;
      }
      startResendTimer();
    });
  }

  /* ── Resend countdown timer ── */

  function startResendTimer() {
    clearResendTimer();
    var resendBtn   = overlay.querySelector('.cs-otp-resend__btn');
    var resendTimer = overlay.querySelector('.cs-otp-resend__timer');
    var strong      = resendTimer ? resendTimer.querySelector('strong') : null;
    var seconds     = 30;

    if (resendBtn)   resendBtn.hidden = true;
    if (resendTimer) resendTimer.hidden = false;

    _resendTimer = setInterval(function () {
      seconds--;
      if (strong) strong.textContent = seconds + 's';

      if (seconds <= 0) {
        clearResendTimer();
        if (resendTimer) resendTimer.hidden = true;
        if (resendBtn)   resendBtn.hidden = false;
      }
    }, 1000);
  }

  function clearResendTimer() {
    if (_resendTimer) { clearInterval(_resendTimer); _resendTimer = null; }
  }

  /* ── OTP digit helpers ── */

  function getOtpValue() {
    var digits = overlay.querySelectorAll('.cs-otp-digit');
    var otp = '';
    digits.forEach(function (d) { otp += d.value.trim(); });
    return otp;
  }

  function clearDigits() {
    overlay.querySelectorAll('.cs-otp-digit').forEach(function (d) {
      d.value = '';
      d.classList.remove('cs-otp-digit--filled');
    });
  }

  /* ── Digit input UX — auto-advance, backspace, paste ── */

  function initDigitInputs() {
    var digits = Array.prototype.slice.call(overlay.querySelectorAll('.cs-otp-digit'));

    digits.forEach(function (digit, idx) {
      digit.addEventListener('input', function (e) {
        // Strip non-numeric
        var val = digit.value.replace(/\D/g, '');
        digit.value = val ? val.charAt(0) : '';
        digit.classList.toggle('cs-otp-digit--filled', !!digit.value);

        if (digit.value && idx < digits.length - 1) {
          digits[idx + 1].focus();
        }
      });

      digit.addEventListener('keydown', function (e) {
        if (e.key === 'Backspace' && !digit.value && idx > 0) {
          digits[idx - 1].focus();
          digits[idx - 1].value = '';
          digits[idx - 1].classList.remove('cs-otp-digit--filled');
        }
        // Allow digits 0-9 and control keys
        if (!/^\d$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
          if (!e.ctrlKey && !e.metaKey) e.preventDefault();
        }
      });

      // Paste: handle full OTP paste into any digit box
      digit.addEventListener('paste', function (e) {
        e.preventDefault();
        var pasted = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '');
        if (!pasted) return;

        // Fill from current digit onwards
        var startIdx = idx;
        for (var i = 0; i < pasted.length && startIdx + i < digits.length; i++) {
          digits[startIdx + i].value = pasted.charAt(i);
          digits[startIdx + i].classList.add('cs-otp-digit--filled');
        }
        // Focus the next empty or last digit
        var nextFocus = Math.min(startIdx + pasted.length, digits.length - 1);
        digits[nextFocus].focus();
      });

      // Select all on focus for easy re-entry
      digit.addEventListener('focus', function () { digit.select(); });
    });
  }

  /* ── Save OTP session to localStorage (fallback for new Shopify accounts) ── */

  function saveSession(data) {
    try {
      localStorage.setItem('cs_otp_session', JSON.stringify({
        phone:        _phone,
        email:        data.email        || '',
        firstName:    data.firstName    || '',
        lastName:     data.lastName     || '',
        sessionToken: data.sessionToken || '',
        expiresAt:    data.expiresAt    || 0
      }));
    } catch (e) {}
  }

  /* ── Try native Shopify login; fall back to /pages/my-account via localStorage ── */

  function doLogin(email, password) {
    showStep('loading');

    // Get CSRF token from homepage (not /account/login — that redirects to Shopify auth)
    fetch('/?section_id=announcement-bar', { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var match = html.match(/name="authenticity_token"[^>]*value="([^"]+)"/)
                 || html.match(/<meta[^>]+name="csrf-token"[^>]+content="([^"]+)"/i);
        var token = match ? match[1] : '';

        if (token) {
          var formToken    = document.getElementById('cs-otp-form-token');
          var formEmail    = document.getElementById('cs-otp-form-email');
          var formPassword = document.getElementById('cs-otp-form-password');
          var form         = document.getElementById('cs-otp-login-form');
          if (formToken)    formToken.value    = token;
          if (formEmail)    formEmail.value    = email;
          if (formPassword) formPassword.value = password;
          if (form) { form.submit(); return; }
        }

        // CSRF not found or form missing — go directly via localStorage session
        window.location.href = '/pages/my-account';
      })
      .catch(function () {
        window.location.href = '/pages/my-account';
      });
  }

  /* ── Validation helpers ── */

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /* ── Intercept guest account icon clicks ── */

  function interceptAccountLinks() {
    document.addEventListener('click', function (e) {
      var el = e.target.closest ? e.target.closest('[data-cs-otp-trigger], a') : null;
      if (!el) return;

      // Explicit trigger attribute — open menu if session valid, else popup
      if (el.hasAttribute('data-cs-otp-trigger')) {
        e.preventDefault();
        e.stopPropagation(); // prevent onclick on element from calling csOtpMenu a second time
        var _s; try { _s = JSON.parse(localStorage.getItem('cs_otp_session')); } catch(_e) {}
        if (_s && _s.sessionToken && Date.now() < _s.expiresAt) {
          if (window.csOtpMenu) window.csOtpMenu(e, el);
        } else {
          openPopup();
        }
        return;
      }

      var href = el.getAttribute('href') || '';
      if (href === '/account/login' || href.indexOf('/account/login') !== -1 || href.indexOf('/customer_authentication') !== -1) {
        e.preventDefault();
        openPopup();
      }
    }, { capture: true });
  }

  /* ── Delegated events inside the popup ── */

  function initPopupEvents() {
    // Close button
    overlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-cs-otp-close]')) {
        closePopup();
        return;
      }

      // Click backdrop
      if (e.target === overlay) {
        closePopup();
        return;
      }

      // Actions
      var trigger = e.target.closest('[data-cs-otp-action]');
      if (!trigger) return;

      var action = trigger.getAttribute('data-cs-otp-action');

      switch (action) {
        case 'send':       handleSendOtp();       break;
        case 'verify':     handleVerifyOtp();     break;
        case 'create':     handleCreateCustomer(); break;
        case 'resend':     handleResend();         break;
        case 'back-phone':
          clearResendTimer();
          showStep('phone');
          break;
      }
    });

    // Keyboard: Enter submits the current step's primary action
    overlay.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;

      switch (_currentStep) {
        case 'phone':   handleSendOtp();       break;
        case 'otp':
          // Only submit if inside a digit or on the verify button
          if (!e.target.classList.contains('cs-otp-digit') && e.target.getAttribute('data-cs-otp-action') !== 'verify') break;
          handleVerifyOtp();
          break;
        case 'profile': handleCreateCustomer(); break;
      }
    });

    // Escape closes popup
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.getAttribute('aria-hidden') === 'false') {
        closePopup();
      }
    });

    // Remove error highlight on profile inputs when user types
    var profileInputs = overlay.querySelectorAll('.cs-otp-field__input');
    profileInputs.forEach(function (input) {
      input.addEventListener('input', function () {
        input.classList.remove('cs-otp-field__input--error');
        resetError('profile');
      });
    });
  }

  /* ── Bootstrap ── */

  function init() {
    initDigitInputs();
    initPopupEvents();
    interceptAccountLinks();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
